import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import {
	type AdminPost,
	cancelScheduledPublication,
	currentChannelPublication,
	getPublishStatus,
	publishAdminPost,
	republishToChannel,
	retryChannelPublication,
	syncChannelPublications,
	unpublishFromChannel,
} from "@/entities/admin-post"

const POLL_MS = 1500

/** Перечитать пост и список: после любого действия меняется его состояние на площадках. */
export async function refreshPost(queryClient: QueryClient, postId: string) {
	await Promise.all([
		queryClient.invalidateQueries({ queryKey: ["admin-post", postId] }),
		queryClient.invalidateQueries({ queryKey: ["admin-posts"] }),
	])
}

export interface ChannelJob {
	progress: number
	message: string
}

/**
 * Всё, что делается с постом в канале. Отправка идёт задачей на сервере: пока она
 * работает, карточка показывает прогресс, а по завершении перечитывает пост.
 */
export function useChannelPublish(post: AdminPost) {
	const queryClient = useQueryClient()
	const [jobId, setJobId] = useState<string | null>(null)
	const [job, setJob] = useState<ChannelJob | null>(null)
	const publication = currentChannelPublication(post)

	const refresh = () => refreshPost(queryClient, post.id)

	useEffect(() => {
		if (!jobId) return
		let stopped = false
		const timer = window.setInterval(async () => {
			try {
				const status = await getPublishStatus(post.id, jobId)
				if (stopped) return
				if (status.state === "completed") {
					setJobId(null)
					setJob(null)
					toast.success("Пост отправлен в канал")
					await refreshPost(queryClient, post.id)
				} else if (status.state === "failed") {
					setJobId(null)
					setJob(null)
					toast.error(status.error || "Telegram не принял пост")
					await refreshPost(queryClient, post.id)
				} else {
					setJob({
						progress: status.progress ?? 0,
						message: status.message || "Отправляем в канал…",
					})
				}
			} catch (error) {
				setJobId(null)
				setJob(null)
				toast.error(extractApiErrorMessage(error, "Не удалось узнать, как идёт отправка"))
			}
		}, POLL_MS)
		return () => {
			stopped = true
			window.clearInterval(timer)
		}
	}, [jobId, post.id, queryClient])

	const follow = (response: { jobId?: string; scheduledAt?: string }, scheduledText: string) => {
		if (response.jobId) {
			setJob({ progress: 0, message: "Ставим в очередь…" })
			setJobId(response.jobId)
		} else {
			toast.success(scheduledText)
			void refresh()
		}
	}

	const onError = (fallback: string) => (error: unknown) =>
		toast.error(extractApiErrorMessage(error, fallback))

	const publish = useMutation({
		mutationFn: (scheduledAt?: Date) =>
			publishAdminPost(post.id, {
				// Новый пост уходит в канал клуба, который задан в настройках API: так
				// локальная и тестовая админки не публикуют в боевой канал.
				channelId: publication?.channelId,
				scheduledAt: scheduledAt?.toISOString(),
			}),
		onSuccess: (response, scheduledAt) =>
			follow(response, scheduledAt ? "Публикация запланирована" : "Пост отправлен в канал"),
		onError: onError("Не удалось отправить пост в канал"),
	})

	const cancel = useMutation({
		mutationFn: () => cancelScheduledPublication(post.id, publication!.id),
		onSuccess: async () => {
			toast.success("Запланированная публикация отменена")
			await refresh()
		},
		onError: onError("Не удалось отменить публикацию"),
	})

	const unpublish = useMutation({
		mutationFn: () => unpublishFromChannel(post.id, publication!.id),
		onSuccess: async () => {
			toast.success("Сообщение удалено из канала")
			await refresh()
		},
		onError: onError("Не удалось снять пост с канала"),
	})

	const republish = useMutation({
		mutationFn: () => republishToChannel(post.id, publication!.id),
		onSuccess: (response) => follow(response, "Пост отправлен заново"),
		onError: onError("Не удалось отправить пост заново"),
	})

	const retry = useMutation({
		mutationFn: () => retryChannelPublication(post.id, publication!.id),
		onSuccess: (response) => follow(response, "Повторная отправка поставлена в очередь"),
		onError: onError("Не удалось повторить отправку"),
	})

	const sync = useMutation({
		mutationFn: () => syncChannelPublications(post.id),
		onSuccess: async () => {
			toast.success("Сообщение в канале обновлено")
			await refresh()
		},
		onError: onError("Не удалось обновить сообщение в канале"),
	})

	const busy =
		!!job ||
		publish.isPending ||
		cancel.isPending ||
		unpublish.isPending ||
		republish.isPending ||
		retry.isPending ||
		sync.isPending

	return { publication, job, busy, publish, cancel, unpublish, republish, retry, sync }
}
